// src/components/molecules/CustomProgressBar.js

import React from "react";
import { Slider, Box } from "@mui/material";
import ProgressMarker from "../atoms/ProgressMarker";
import PropTypes from "prop-types";
import { useTheme } from "styled-components";

const CustomProgressBar = ({
  currentTime,
  duration,
  interactions,
  onSeek,
  onMarkerClick,
  onAddInteraction, // New prop
}) => {
  const theme = useTheme();
  const marks = interactions.map((interaction) => ({
    value: interaction.timestamp,
    label: "", // Labels can be customized or omitted
    interaction: interaction,
  }));

  const handleChange = (event, newValue) => {
    onSeek(newValue);
  };

  const handleClick = (event) => {
    if (!isFinite(duration) || duration === 0) {
      console.warn("Cannot add interaction: duration is not set or zero.");
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;

    if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
      onAddInteraction(newTime); // Pass the calculated timestamp
    } else {
      console.warn("Calculated timestamp is invalid:", newTime);
    }
  };

  return (
    <Box
      sx={{ position: "relative", width: "100%", padding: "0 10px" }}
      onClick={handleClick} // Capture click events on the progress bar
    >
      <Slider
        min={0}
        max={duration}
        value={currentTime}
        onChange={handleChange}
        aria-labelledby="custom-progress-bar"
        sx={{
          color: theme.colors.green,
          height: 8,
          "& .MuiSlider-thumb": {
            display: "none", // Hide the default thumb
          },
          "& .MuiSlider-track": {
            height: 8,
          },
          "& .MuiSlider-rail": {
            height: 8,
          },
        }}
      />
      {/* Render Markers */}
      {marks.map((mark, index) => (
        <ProgressMarker
          key={index}
          timestamp={mark.value}
          interaction={mark.interaction}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the progress bar click
            onMarkerClick(mark.value);
          }}
          // Calculate the position based on the timestamp
          style={{
            position: "absolute",
            left: `${(mark.value / duration) * 100}%`,
            top: "7px",
            transform: "translateX(-50%)",
          }}
        />
      ))}
    </Box>
  );
};

CustomProgressBar.propTypes = {
  currentTime: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  interactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      mediaURL: PropTypes.string,
      mediaType: PropTypes.oneOf(["image", "video"]),
      callToAction: PropTypes.string,
      callToActionURL: PropTypes.string,
      timestamp: PropTypes.number.isRequired,
    })
  ).isRequired,
  onSeek: PropTypes.func.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  onAddInteraction: PropTypes.func.isRequired, // New prop
};

export default CustomProgressBar;